import React, { useState } from 'react';

import type { LicenceSchema } from '../../../types/schema';
import type { LicenceSortType, LicenceStatus, SortOrder } from '../utilities/types';

import { classes, style } from 'typestyle';
import { mediaDesktop, mediaSmallTablet } from '../utilities/mediaQueries';
import { spacing } from '../../../theme/spacing';
import { colorWhite, rebrand } from '../../../theme/color';
import { px } from 'csx';
import { Loading } from '../../loading/loading';
import { ComponentProps, deps, inject, observer } from '../../../lib/component';
import { LoadMoreArrowButton } from '../atoms/button/LoadMoreArrow';
import { OrderLicence } from '../molecules/OrderLicence';
import { borderRadius } from '../../../theme/border';
import { StyledSelect } from '../molecules/StyledSelect';
import { betweenJustified, center, flexRoot } from 'csstips';
import { DarkRadioInput } from '../atoms/controls/DarkRadioInput';
import { transitionQuickEase } from '../../../theme/transition';

interface LicencesListProps extends ComponentProps {
  licences: LicenceSchema[];
}

const styles = {
  listHeader: style(
    {
      fontWeight: 700,
    },
    mediaSmallTablet(flexRoot, betweenJustified, center, {
      gap: spacing.DEFAULT,
    })
  ),
  statusFilters: style(
    flexRoot,
    center,
    {
      width: '100%',
      padding: '8px 16px',
      backgroundColor: rebrand.neutralOnDark[10].toHexString(),
      borderRadius: borderRadius.MEDIUM,
      flexWrap: 'wrap',
      gap: spacing.XSMALL,
    },
    mediaSmallTablet({
      width: 'auto',
    })
  ),
  statusFilter: (backgroundColor: string) =>
    style(flexRoot, center, {
      cursor: 'pointer',
      padding: '4px 8px',
      fontWeight: 400,
      borderRadius: borderRadius.SMALL,
      transition: `background-color ${transitionQuickEase}`,
      $nest: {
        '& span': {
          backgroundColor,
        },
        '&:hover, &:focus-within': {
          backgroundColor,
        },
      },
    }),
  radioButton: style({
    marginLeft: '6px',
    flexShrink: 0,
  }),
  sortSelect: style(
    {
      marginLeft: 'auto',
      width: px(250),
      marginTop: spacing.XLARGE,
    },
    mediaSmallTablet({
      marginTop: 0,
    })
  ),
  list: style(
    {
      marginTop: spacing.DEFAULT,
    },
    mediaDesktop({
      marginTop: spacing.XLARGE,
    })
  ),
  paginationContainer: style({
    display: 'flex',
    gap: spacing.DEFAULT,
    marginTop: spacing.DEFAULT,
    padding: `${spacing.DEFAULT} 0`,
  }),
  count: style({
    fontSize: '12px',
    margin: '0 0 0 auto',
    color: rebrand.neutralOnLight[70].toString(),
  }),
};

interface SortOption {
  label: string;
  value: {
    sortType: LicenceSortType;
    sortOrder: SortOrder;
  };
}

const sortOptions: SortOption[] = [
  {
    label: 'Newest first',
    value: { sortType: 'date', sortOrder: 'desc' },
  },
  {
    label: 'Oldest first',
    value: { sortType: 'date', sortOrder: 'asc' },
  },
  {
    label: 'Alphabetical A-Z',
    value: { sortType: 'track', sortOrder: 'asc' },
  },
  {
    label: 'Alphabetical Z-A',
    value: { sortType: 'track', sortOrder: 'desc' },
  },
];

interface StatusOption {
  label: string;
  value: LicenceStatus;
  color: string;
}

const statusOptions: StatusOption[] = [
  {
    label: 'All',
    value: 'all',
    color: colorWhite.toString(),
  },
  {
    label: 'Ready to use',
    value: 'unused',
    color: rebrand.semantic.success[20].toString(),
  },
  {
    label: 'Used',
    value: 'used',
    color: rebrand.neutralOnDark[30].toString(),
  },
];

export const LicencesList: React.FC<LicencesListProps> = inject(deps)(
  observer(({ licences, model, controller }) => {
    const [selectedSortOption, setSelectedSortOption] = useState(sortOptions[0]);
    const [selectedStatus, setSelectedStatus] = useState(statusOptions[0]);

    const { loading, loadingMore, pagination } = model.page.accountLicences;
    const { loadMoreLicences, changeSortOrder, filterByStatus } = controller.page.accountLicences;

    const showLoadMore = pagination && licences.length < pagination.total;

    const handleChangeStatus = (status: StatusOption) => {
      setSelectedStatus(status);
      filterByStatus(status.value);
    };

    const handleChangeOrder = (sortOption: SortOption) => {
      setSelectedSortOption(sortOption);
      changeSortOrder(sortOption.value);
    };

    return (
      <div>
        <div className={styles.listHeader}>
          <div className={styles.statusFilters}>
            {statusOptions.map((status) => (
              <label className={classes(styles.statusFilter(status.color))} key={status.label}>
                {status.label}
                <DarkRadioInput
                  className={styles.radioButton}
                  value={status.value}
                  name="license-status"
                  disabled={loading}
                  checked={selectedStatus === status}
                  onChange={() => handleChangeStatus(status)}
                  data-test-license-status-filter={status.label}
                />
              </label>
            ))}
          </div>

          <StyledSelect
            aria-label="License sort order"
            inputId="license_sort_order"
            name="license_sort_order"
            className={styles.sortSelect}
            options={sortOptions}
            value={selectedSortOption}
            onChange={handleChangeOrder}
          />
        </div>

        <div className={styles.list}>
          {loading ? (
            <Loading />
          ) : licences.length > 0 ? (
            licences.map((licence) => <OrderLicence licence={licence} key={licence.identity} showStatusFlag />)
          ) : (
            <p>There are no orders for this channel</p>
          )}
        </div>

        {!loading && licences.length > 0 && pagination ? (
          <div className={styles.paginationContainer}>
            {showLoadMore && (
              <LoadMoreArrowButton hidden={loading} loading={loadingMore} handleLoadMore={loadMoreLicences} />
            )}

            <span className={styles.count}>
              Showing {licences.length} of {pagination.total}
            </span>
          </div>
        ) : null}
      </div>
    );
  })
);
